import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ChevronRight,
  Search,
  AlertTriangle,
  Package,
  Layers,
  Upload,
  CheckCircle2,
} from "lucide-react";
import { useAuth } from "../../utils/auth";
import { canCreateTickets } from "../../utils/roles";
import { searchOrdersByPhone } from "../../api/OrderAPIs";
import {
  createTicket,
  fetchActiveTicketsForOrder,
  fetchTicketOrderContext,
  fetchTicketProblemTypes,
  fetchAssignableUsers,
  fileToTicketAttachment,
  TICKET_PRIORITIES,
} from "../../api/TicketAPIs";
import { AttachmentPreview, TicketStatusBadge, TicketPriorityBadge } from "./TicketBadges";
import {
  normalizePhoneNumber,
  PRIORITY_LABELS_AR,
  formatDateTime,
  resolveMediaUrl,
} from "./ticketUi";

const emptyIssueForm = () => ({
  problemTypeId: "",
  description: "",
  attachments: [],
});

const mapSearchOrderToContext = (order) => {
  if (!order) return null;
  const details = order.orderDetails || order.OrderDetails || [];
  return {
    orderId: order.orderId ?? order.OrderId,
    name: order.name ?? order.Name,
    phoneNumber: order.phoneNumber ?? order.PhoneNumber,
    phoneNumber2: order.phoneNumber2 ?? order.PhoneNumber2,
    status: order.status ?? order.Status,
    date: order.date ?? order.Date,
    totalAmount: order.totalAmount ?? order.TotalAmount,
    itemCount: details.reduce((sum, d) => sum + (d.quantity ?? d.Quantity ?? 0), 0),
    address: order.address || order.Address || null,
    items: details.map((od) => {
      const design = od.design || od.Design;
      const type = od.type || od.Type;
      const product = od.product || od.Product;
      const variant = od.productVariant || od.ProductVariant;
      const brandName = type?.brand?.name ?? type?.Brand?.Name;
      const typeName = type?.name ?? type?.Name;
      return {
        orderDetailsId: od.orderDetailsId ?? od.OrderDetailsId,
        designId: od.designId ?? od.DesignId,
        typeId: od.typeId ?? od.TypeId,
        productId: od.productId ?? od.ProductId,
        productVariantId: od.productVariantId ?? od.ProductVariantId,
        quantity: od.quantity ?? od.Quantity,
        price: od.price ?? od.Price,
        customNameAr: od.customNameAr ?? od.CustomNameAr,
        customNameEn: od.customNameEn ?? od.CustomNameEn,
        designCode: design?.code ?? design?.Code,
        designName: design?.name ?? design?.Name,
        imageUrl: design?.imageUrl_1 ?? design?.ImageUrl_1 ?? product?.imageUrl ?? product?.ImageUrl,
        productName: product?.name ?? product?.Name ?? variant?.product?.name ?? variant?.Product?.Name,
        variantCode: variant?.code ?? variant?.Code,
        productModel: brandName ? `${brandName} / ${typeName || ""}`.trim() : typeName,
        itemType: typeName,
        orderItemType: od.itemType ?? od.ItemType,
        brandName,
      };
    }),
  };
};

const orderField = (ctx, camel, pascal) => ctx?.[camel] ?? ctx?.[pascal];

const CreateTicketPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { authState } = useAuth();
  const canCreate = canCreateTickets(authState.role);
  const prefillOrderRef = useRef(null);
  const selectOrderRef = useRef(null);

  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState("");
  const [searching, setSearching] = useState(false);
  const [orders, setOrders] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [orderContext, setOrderContext] = useState(null);
  const [activeTickets, setActiveTickets] = useState([]);
  const [scope, setScope] = useState("order"); // order | items
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [orderIssue, setOrderIssue] = useState(emptyIssueForm());
  const [itemIssues, setItemIssues] = useState({});
  const [priority, setPriority] = useState("Medium");
  const [assignedToUserId, setAssignedToUserId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [ackDuplicates, setAckDuplicates] = useState(false);
  const [prefilling, setPrefilling] = useState(() => {
    const raw = new URLSearchParams(window.location.search).get("orderId");
    return !!raw && /^\d+$/.test(raw);
  });

  const { data: problemTypes = [] } = useQuery({
    queryKey: ["ticketProblemTypes", "active"],
    queryFn: () => fetchTicketProblemTypes(true),
    enabled: canCreate,
  });

  const { data: users = [] } = useQuery({
    queryKey: ["ticketAssignableUsers"],
    queryFn: fetchAssignableUsers,
    enabled: canCreate,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!authState.isValidating && !canCreate) navigate("/login");
  }, [authState, canCreate, navigate]);

  const handleSearch = async () => {
    const normalized = normalizePhoneNumber(phone);
    if (!normalized || normalized.length < 8) {
      toast.error("أدخل رقم هاتف صحيح");
      return;
    }
    setSearching(true);
    try {
      const result = await searchOrdersByPhone(normalized);
      setOrders(Array.isArray(result) ? result : []);
      if (!result?.length) toast.info("لا توجد طلبات لهذا الرقم");
    } catch (err) {
      toast.error(err.message || "فشل البحث");
    } finally {
      setSearching(false);
    }
  };

  const selectOrder = async (orderId) => {
    setSelectedOrderId(orderId);
    setAckDuplicates(false);
    const searched = orders.find((o) => (o.orderId ?? o.OrderId) === orderId);
    try {
      let ctx = null;
      let active = [];
      try {
        ctx = await fetchTicketOrderContext(orderId);
      } catch {
        ctx = mapSearchOrderToContext(searched);
      }
      try {
        active = await fetchActiveTicketsForOrder(orderId);
      } catch {
        active = [];
      }
      if (!ctx) {
        toast.error("فشل تحميل الطلب");
        return;
      }
      setOrderContext(ctx);
      setActiveTickets(Array.isArray(active) ? active : []);
      setSelectedItemIds([]);
      setItemIssues({});
      setOrderIssue(emptyIssueForm());
      setScope("order");
      setStep(2);
    } catch (err) {
      toast.error(err.message || "فشل تحميل الطلب");
    } finally {
      setPrefilling(false);
    }
  };

  selectOrderRef.current = selectOrder;

  useEffect(() => {
    if (!canCreate || authState.isValidating) return;
    const raw = searchParams.get("orderId");
    if (!raw || !/^\d+$/.test(raw)) {
      setPrefilling(false);
      return;
    }
    if (prefillOrderRef.current === raw) return;
    prefillOrderRef.current = raw;
    const phoneParam = searchParams.get("phone");
    if (phoneParam) setPhone(phoneParam);
    selectOrderRef.current?.(Number(raw));
  }, [canCreate, authState.isValidating, searchParams]);

  const toggleItem = (orderDetailsId) => {
    setSelectedItemIds((prev) => {
      const exists = prev.includes(orderDetailsId);
      const next = exists ? prev.filter((id) => id !== orderDetailsId) : [...prev, orderDetailsId];
      setItemIssues((issues) => {
        const copy = { ...issues };
        if (exists) delete copy[orderDetailsId];
        else if (!copy[orderDetailsId]) copy[orderDetailsId] = emptyIssueForm();
        return copy;
      });
      return next;
    });
  };

  const updateItemIssue = (id, patch) => {
    setItemIssues((prev) => ({
      ...prev,
      [id]: { ...prev[id], ...patch },
    }));
  };

  const addAttachments = async (files, target) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    try {
      const mapped = [];
      for (const file of list) {
        const isVideo = (file.type || "").startsWith("video/");
        const isImage = (file.type || "").startsWith("image/");
        if (!isVideo && !isImage) {
          toast.error(`نوع ملف غير مدعوم: ${file.name}`);
          continue;
        }
        const max = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
        if (file.size > max) {
          toast.error(`الملف كبير جداً: ${file.name}`);
          continue;
        }
        mapped.push(await fileToTicketAttachment(file));
      }
      if (target === "order") {
        setOrderIssue((prev) => ({ ...prev, attachments: [...prev.attachments, ...mapped] }));
      } else {
        setItemIssues((prev) => ({
          ...prev,
          [target]: {
            ...(prev[target] || emptyIssueForm()),
            attachments: [...(prev[target]?.attachments || []), ...mapped],
          },
        }));
      }
    } catch (err) {
      toast.error(err.message || "فشل قراءة الملفات");
    }
  };

  const validateAndSubmit = async () => {
    if (!selectedOrderId) {
      toast.error("اختر طلباً");
      return;
    }

    let issues = [];
    if (scope === "order") {
      if (!orderIssue.problemTypeId || !orderIssue.description.trim()) {
        toast.error("أدخل نوع المشكلة والوصف للطلب");
        return;
      }
      issues = [
        {
          orderDetailsId: null,
          problemTypeId: Number(orderIssue.problemTypeId),
          description: orderIssue.description.trim(),
          attachments: orderIssue.attachments.map(({ fileName, fileType, base64Content }) => ({
            fileName,
            fileType,
            base64Content,
          })),
        },
      ];
    } else {
      if (!selectedItemIds.length) {
        toast.error("اختر عنصراً واحداً على الأقل");
        return;
      }
      for (const id of selectedItemIds) {
        const issue = itemIssues[id];
        if (!issue?.problemTypeId || !issue.description?.trim()) {
          toast.error("أكمل نوع المشكلة والوصف لكل عنصر محدد");
          return;
        }
        issues.push({
          orderDetailsId: id,
          problemTypeId: Number(issue.problemTypeId),
          description: issue.description.trim(),
          attachments: (issue.attachments || []).map(({ fileName, fileType, base64Content }) => ({
            fileName,
            fileType,
            base64Content,
          })),
        });
      }
    }

    if (activeTickets.length > 0 && !ackDuplicates) {
      toast.warning("يوجد تذاكر نشطة لهذا الطلب — أكّد المتابعة أولاً");
      return;
    }

    setSubmitting(true);
    try {
      const ticket = await createTicket({
        orderId: selectedOrderId,
        priority,
        assignedToUserId: assignedToUserId ? Number(assignedToUserId) : null,
        issues,
      });
      toast.success("تم إنشاء التذكرة");
      queryClient.invalidateQueries({ queryKey: ["ticketStatusCounts"] });
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
      navigate(`/tickets/${ticket.id}`);
    } catch (err) {
      toast.error(err.message || "فشل إنشاء التذكرة");
    } finally {
      setSubmitting(false);
    }
  };

  if (!canCreate) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4 md:p-8" dir="rtl">
      <ToastContainer position="top-center" />
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate("/tickets")} className="p-2 rounded-xl bg-white shadow">
            <ChevronRight size={22} />
          </button>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">إنشاء تذكرة جديدة</h1>
            <p className="text-gray-500">الخطوة {step} من 3</p>
          </div>
        </div>

        <div className="flex gap-2 mb-6">
          {[1, 2, 3].map((n) => (
            <div
              key={n}
              className={`flex-1 h-2 rounded-full ${step >= n ? "bg-indigo-600" : "bg-gray-200"}`}
            />
          ))}
        </div>

        {prefilling && step === 1 && (
          <div className="bg-white/90 rounded-2xl shadow border border-gray-100 p-8 text-center text-gray-600">
            جاري تحميل الطلب...
          </div>
        )}

        {step === 1 && !prefilling && (
          <div className="bg-white/90 rounded-2xl shadow border border-gray-100 p-5 space-y-4">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Search size={20} /> البحث عن العميل والطلب
            </h2>
            <div className="flex flex-col md:flex-row gap-3">
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                placeholder="رقم هاتف العميل"
                className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl"
              />
              <button
                onClick={handleSearch}
                disabled={searching}
                className="px-5 py-3 rounded-xl bg-indigo-600 text-white font-semibold disabled:opacity-50"
              >
                {searching ? "جاري البحث..." : "بحث"}
              </button>
            </div>

            <div className="space-y-3">
              {orders.map((o) => (
                <button
                  key={o.orderId}
                  onClick={() => selectOrder(o.orderId)}
                  className="w-full text-right bg-gray-50 hover:bg-indigo-50 border border-gray-200 rounded-xl p-4 transition"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-bold text-indigo-700">طلب #{o.orderId}</span>
                    <span className="text-sm px-2 py-1 rounded-full bg-white border">{o.status}</span>
                  </div>
                  <div className="mt-2 text-sm text-gray-700">
                    {o.name} — {o.phoneNumber}
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    {formatDateTime(o.date)} — {o.orderDetails?.length || 0} أسطر منتجات
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && orderContext && (
          <div className="space-y-4">
            <OrderContextBanner context={orderContext} />
            <div className="bg-white/90 rounded-2xl shadow border border-gray-100 p-5">
              <h2 className="text-xl font-bold mb-3">نطاق المشكلة</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button
                  onClick={() => setScope("order")}
                  className={`p-4 rounded-xl border-2 text-right ${
                    scope === "order" ? "border-indigo-600 bg-indigo-50" : "border-gray-200"
                  }`}
                >
                  <Layers className="mb-2 text-indigo-600" />
                  <div className="font-bold">الطلب بالكامل</div>
                  <div className="text-sm text-gray-500">مشكلة عامة على مستوى الطلب</div>
                </button>
                <button
                  onClick={() => setScope("items")}
                  className={`p-4 rounded-xl border-2 text-right ${
                    scope === "items" ? "border-indigo-600 bg-indigo-50" : "border-gray-200"
                  }`}
                >
                  <Package className="mb-2 text-indigo-600" />
                  <div className="font-bold">عناصر محددة</div>
                  <div className="text-sm text-gray-500">اختر منتجاً أو أكثر من الطلب</div>
                </button>
              </div>

              {scope === "items" && (
                <div className="mt-4 space-y-3">
                  {(orderContext.items || []).map((item) => {
                    const selected = selectedItemIds.includes(item.orderDetailsId);
                    return (
                      <label
                        key={item.orderDetailsId}
                        className={`flex gap-3 p-3 rounded-xl border cursor-pointer ${
                          selected ? "border-indigo-500 bg-indigo-50/60" : "border-gray-200"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleItem(item.orderDetailsId)}
                          className="mt-2"
                        />
                        <img
                          src={resolveMediaUrl(item.imageUrl)}
                          alt=""
                          className="w-16 h-16 rounded-lg object-cover bg-gray-100"
                        />
                        <div className="flex-1 text-sm">
                          <div className="font-semibold">
                            {item.productName || item.designName || item.designCode || "قطعة"}
                          </div>
                          <div className="text-gray-600">
                            {[item.designCode, item.productModel].filter(Boolean).join(" — ")}
                          </div>
                          <div className="text-gray-500">النوع: {item.itemType} — الكمية: {item.quantity}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}

              <div className="flex justify-between mt-5">
                <button onClick={() => setStep(1)} className="px-4 py-2 rounded-xl border">
                  رجوع
                </button>
                <button
                  onClick={() => {
                    if (scope === "items" && !selectedItemIds.length) {
                      toast.error("اختر عنصراً واحداً على الأقل");
                      return;
                    }
                    setStep(3);
                  }}
                  className="px-5 py-2 rounded-xl bg-indigo-600 text-white"
                >
                  التالي
                </button>
              </div>
            </div>
          </div>
        )}

        {step === 3 && orderContext && (
          <div className="space-y-4">
            <OrderContextBanner context={orderContext} />
            {activeTickets.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
                <div className="flex items-start gap-2 text-amber-800 font-semibold">
                  <AlertTriangle size={18} className="mt-0.5" />
                  يوجد {activeTickets.length} تذكرة نشطة لهذا الطلب
                </div>
                <div className="mt-3 space-y-2">
                  {activeTickets.map((t) => (
                    <div key={t.id} className="bg-white rounded-xl p-3 text-sm flex flex-wrap gap-2 items-center">
                      <span className="font-bold">#{t.id}</span>
                      <TicketStatusBadge status={t.status} />
                      <TicketPriorityBadge priority={t.priority} />
                      <span className="text-gray-500">{formatDateTime(t.createdAtUtc)}</span>
                      <button
                        type="button"
                        className="text-indigo-600 underline"
                        onClick={() => navigate(`/tickets/${t.id}`)}
                      >
                        عرض
                      </button>
                    </div>
                  ))}
                </div>
                <label className="flex items-center gap-2 mt-3 text-sm">
                  <input
                    type="checkbox"
                    checked={ackDuplicates}
                    onChange={(e) => setAckDuplicates(e.target.checked)}
                  />
                  أفهم ذلك وأرغب بإنشاء تذكرة جديدة لمشكلة مختلفة
                </label>
              </div>
            )}

            <div className="bg-white/90 rounded-2xl shadow border border-gray-100 p-5 space-y-4">
              <h2 className="text-xl font-bold">تفاصيل المشكلة</h2>

              {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-gray-600">الأولوية</label>
                  <select
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-xl"
                  >
                    {TICKET_PRIORITIES.map((p) => (
                      <option key={p} value={p}>
                        {PRIORITY_LABELS_AR[p]}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-600">تعيين إلى (اختياري)</label>
                  <select
                    value={assignedToUserId}
                    onChange={(e) => setAssignedToUserId(e.target.value)}
                    className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-xl"
                  >
                    <option value="">بدون تعيين</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.email} ({u.role})
                      </option>
                    ))}
                  </select>
                </div>
              </div> */}

              {scope === "order" ? (
                <IssueEditor
                  title="مشكلة على مستوى الطلب"
                  value={orderIssue}
                  onChange={setOrderIssue}
                  problemTypes={problemTypes}
                  onAddFiles={(files) => addAttachments(files, "order")}
                />
              ) : (
                selectedItemIds.map((id) => {
                  const item = (orderContext.items || []).find((i) => i.orderDetailsId === id);
                  return (
                    <IssueEditor
                      key={id}
                      title={`${item?.productName || item?.designName || item?.designCode || "عنصر"}${item?.productModel ? ` — ${item.productModel}` : ""}`}
                      previewUrl={resolveMediaUrl(item?.imageUrl)}
                      meta={`النوع: ${item?.itemType || "—"} | الكمية: ${item?.quantity || 1}`}
                      value={itemIssues[id] || emptyIssueForm()}
                      onChange={(v) => updateItemIssue(id, v)}
                      problemTypes={problemTypes}
                      onAddFiles={(files) => addAttachments(files, id)}
                    />
                  );
                })
              )}

              <div className="flex justify-between pt-2">
                <button onClick={() => setStep(2)} className="px-4 py-2 rounded-xl border">
                  رجوع
                </button>
                <button
                  onClick={validateAndSubmit}
                  disabled={submitting}
                  className="px-5 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold flex items-center gap-2 disabled:opacity-50"
                >
                  <CheckCircle2 size={18} />
                  {submitting ? "جاري الإنشاء..." : "إنشاء التذكرة"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

function OrderContextBanner({ context }) {
  const orderId = orderField(context, "orderId", "OrderId");
  const phone = orderField(context, "phoneNumber", "PhoneNumber");
  const name = orderField(context, "name", "Name");
  return (
    <div className="bg-white/90 rounded-2xl shadow border border-indigo-100 p-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
      <div>
        <div className="text-xs text-gray-500">رقم الطلب</div>
        <div className="text-xl font-bold text-indigo-700">#{orderId}</div>
      </div>
      <div>
        <div className="text-xs text-gray-500">رقم الموبايل</div>
        <div className="font-semibold text-gray-800">{phone || "—"}</div>
      </div>
      <div>
        <div className="text-xs text-gray-500">العميل</div>
        <div className="font-semibold text-gray-800">{name || "—"}</div>
      </div>
    </div>
  );
}

function IssueEditor({ title, previewUrl, meta, value, onChange, problemTypes, onAddFiles }) {
  return (
    <div className="border border-gray-200 rounded-2xl p-4 bg-gray-50/70 space-y-3">
      <div className="flex gap-3 items-start">
        {previewUrl && (
          <img src={previewUrl} alt="" className="w-16 h-16 rounded-lg object-cover bg-white" />
        )}
        <div>
          <div className="font-bold text-gray-800">{title}</div>
          {meta && <div className="text-xs text-gray-500 mt-1">{meta}</div>}
        </div>
      </div>
      <select
        value={value.problemTypeId}
        onChange={(e) => onChange({ ...value, problemTypeId: e.target.value })}
        className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl bg-white"
      >
        <option value="">اختر نوع المشكلة</option>
        {problemTypes.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>
      <textarea
        rows={3}
        value={value.description}
        onChange={(e) => onChange({ ...value, description: e.target.value })}
        placeholder="وصف المشكلة..."
        className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl bg-white"
      />
      <div>
        <label className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-dashed border-gray-300 bg-white cursor-pointer text-sm">
          <Upload size={16} />
          رفع صور / فيديو
          <input
            type="file"
            accept="image/*,video/*"
            multiple
            className="hidden"
            onChange={(e) => {
              onAddFiles(e.target.files);
              e.target.value = "";
            }}
          />
        </label>
        <div className="flex flex-wrap gap-2 mt-2">
          {(value.attachments || []).map((att, idx) => (
            <AttachmentPreview
              key={`${att.fileName}-${idx}`}
              attachment={att}
              onRemove={() =>
                onChange({
                  ...value,
                  attachments: value.attachments.filter((_, i) => i !== idx),
                })
              }
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default CreateTicketPage;
